import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import { ReportProblemOutlined, DoubleArrow } from '@mui/icons-material';
import { LegacyAuditLogRecord, AuditLogConfig, AuditLogContentConfig } from './AuditLogPanel.types';
import { getActionConfig } from './AuditLogPanel.utils';
import AuditLogCellValue from './AuditLogCellValue';

interface Props {
	record: LegacyAuditLogRecord;
	config: AuditLogConfig;
	seeUnchangedData: boolean;
}

const useStyles = makeStyles(() => ({
	table: {
		tableLayout: 'fixed',
		'& tr:last-child td': { borderBottom: 'none' },
		'& td:last-child': {
			maxWidth: 0,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		},
	},
	cell: {
		padding: '5px 8px',
		fontSize: '1.3rem',
		borderColor: 'oklch(93% 0.005 85)',
		color: 'oklch(35% 0.010 55)',
	},
	added: {
		backgroundColor: 'oklch(97% 0.022 145)',
	},
	removed: {
		backgroundColor: 'oklch(97.5% 0.016 22)',
	},
	addedText: {
		color: 'oklch(37% 0.09 145)',
		display: 'block',
	},
	removedText: {
		color: 'oklch(41% 0.09 22)',
		display: 'block',
		textDecoration: 'line-through',
		opacity: 0.8,
	},
	hidden: {
		display: 'none',
	},
	warningIcon: {
		color: 'oklch(72% 0.12 80)',
		height: 16,
		width: 16,
		verticalAlign: 'text-bottom',
		marginLeft: 4,
	},
	simpleView: {
		display: 'flex',
		justifyContent: 'space-around',
		alignItems: 'center',
		padding: '8px 0',
		fontSize: '0.875rem',
		color: 'oklch(35% 0.010 55)',
	},
}));

function isObject(obj: unknown): obj is Record<string, unknown> {
	return obj instanceof Object && typeof obj === 'object' && !(obj instanceof Array);
}

function capitalize(str: string): string {
	return str.charAt(0).toUpperCase() + str.slice(1);
}

function AuditLogLegacyContent({ record, config, seeUnchangedData }: Props) {
	const classes = useStyles();
	const { actions } = config;
	const cfg = getActionConfig(record.action, actions);

	function getRowClass(changeType: string): string {
		if (changeType === 'added') return classes.added;
		if (changeType === 'removed') return classes.removed;
		if (changeType === 'noChange' && !seeUnchangedData) return classes.hidden;
		return '';
	}

	function getAutomaticFields(): string[] {
		if (cfg?.automaticFields && typeof cfg.automaticFields !== 'boolean') {
			return cfg.automaticFields;
		}
		return [];
	}

	function getCellData(
		before: Record<string, unknown> | null,
		after: Record<string, unknown> | null,
		changes: Record<string, unknown>,
		key: string
	): React.ReactNode {
		if (after && Array.isArray(after[key])) return '-array-';

		if (changes[key] === 'changed') {
			return (
				<>
					<span className={classes.removedText}>
						<AuditLogCellValue value={before ? before[key] : undefined} />
					</span>
					<span className={classes.addedText}>
						<AuditLogCellValue value={after ? after[key] : undefined} />
					</span>
				</>
			);
		}
		if (changes[key] === 'removed') {
			return <AuditLogCellValue value={before ? before[key] : undefined} />;
		}
		if (changes[key] === 'added') {
			return <AuditLogCellValue value={after ? after[key] : undefined} />;
		}
		return '\u2014';
	}

	function getElements(
		type: string,
		before: Record<string, unknown> | null,
		after: Record<string, unknown> | null,
		changes: Record<string, unknown>,
		automaticFields: string[]
	): React.ReactElement[] {
		const elements: React.ReactElement[] = [];
		Object.keys(changes).forEach((key, index) => {
			if (isObject(changes[key])) {
				elements.push(
					...getElements(
						type,
						before && before[key] ? (before[key] as Record<string, unknown>) : null,
						after && after[key] ? (after[key] as Record<string, unknown>) : null,
						changes[key] as Record<string, unknown>,
						automaticFields
					)
				);
			} else {
				elements.push(
					<TableRow
						key={`${type}-${changes[key]}-${key}-${index}`}
						className={getRowClass(changes[key] as string)}
					>
						<TableCell className={classes.cell}>
							{capitalize(key)}
							{automaticFields.includes(key) && (
								<Tooltip placement="top" title="Dette felt er blevet autogeneret af systemet">
									<ReportProblemOutlined className={classes.warningIcon} />
								</Tooltip>
							)}
						</TableCell>
						<TableCell className={classes.cell} align="right">
							{getCellData(before, after, changes, key)}
						</TableCell>
					</TableRow>
				);
			}
		});
		return elements;
	}

	function getSimpleData(
		before: Record<string, unknown> | null,
		after: Record<string, unknown> | null,
		changes: Record<string, unknown>,
		field: string | undefined
	): { before: unknown; after: unknown } {
		let result: { before: unknown; after: unknown } = { before: '', after: '' };
		Object.keys(changes).forEach(key => {
			if (typeof changes[key] === 'object') {
				result = getSimpleData(
					before ? (before[key] as Record<string, unknown>) : null,
					after ? (after[key] as Record<string, unknown>) : null,
					changes[key] as Record<string, unknown>,
					field
				);
			}
			if (changes[key] !== 'noChange' && (!field || key === field)) {
				result = { before: before && before[key] ? before[key] : null, after: after ? after[key] : null };
			}
		});
		return result;
	}

	if (cfg?.content) {
		const contentCfg: AuditLogContentConfig = cfg.content(record);
		if (contentCfg.type === 'simple') {
			const field = (contentCfg as any).field as string | undefined;
			const { before: bVal, after: aVal } = getSimpleData(record.before, record.after, record.changes, field);
			if (!bVal && !aVal) return null;
			return (
				<div className={classes.simpleView}>
					<span>{String(bVal ?? '\u2014')}</span>
					<DoubleArrow fontSize="small" style={{ color: 'oklch(63% 0.007 55)', margin: '0 8px' }} />
					<span>{String(aVal ?? '\u2014')}</span>
				</div>
			);
		}
	}

	const elements = getElements(
		record.action,
		record.before,
		record.after,
		record.changes,
		getAutomaticFields()
	);

	if (elements.length === 0) return null;

	return (
		<Table className={classes.table} size="small" aria-label="a dense table">
			<TableBody>{elements}</TableBody>
		</Table>
	);
}

export default AuditLogLegacyContent;
